<script lang="ts" setup>
import { ref } from 'vue'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { getErrorCode } from '@/app/core/composable/general'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { DBtn, DTextField, DTable, DLoading, DModal } from 'front-library'
import { getVacationAccumulationRequests } from '@/app/core/services/vacation-accumulation-requests'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
usePayrollChange(() => window.location.reload())

/* notifications */
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableVacationAccumulationRequests = ref(null)
const urlFile = ref(null)
const refresh = ref()
const payload = ref()

/* Datos componente table */
const columns = ref([
  {
    name: 'employeeName',
    title: t('vacationAccumulationRequests.list.employee'),
    table: 'employees.name'
  },
  {
    name: 'policyCode',
    title: t('vacationAccumulationRequests.list.policyCode'),
    table: 'vacation_policies.code'
  },
  {
    name: 'statusProcess',
    title: t('vacationAccumulationRequests.list.status'),
    table: 'process_status.name'
  },
  {
    name: 'listResult',
    title: t('vacationAccumulationRequests.list.result')
  },
  {
    name: 'created_at',
    title: t('vacationAccumulationRequests.list.created_at'),
    table: 'vacation_accumulation_requests.created_at'
  }
])
const rows = ref<any[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: payload.value.fields.filter(
          (field: any) => !field.includes('statusCode')
        ),
        headers: columns.value
          .filter((item: any) => item.title !== '')
          .map((item: any) => item.title),
        module: t('vacationAccumulationRequests.list.title').replace('ó', 'o')
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    loading.value = false
    const code = getErrorCode(error.response.data?.code)
    notifications.addNotification({
      title: t('error.titles.' + code),
      text: t('error.messages.' + code),
      type: 'error',
      time: 10000
    })
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  urlFile.value = null
}

/* Listar */
const vacationAccumulationRequests = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getVacationAccumulationRequests(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    payload.value = data[1]
    loading.value = false
  } catch (error: any) {
    loading.value = false
    const code = getErrorCode(error.response.data?.code)
    notifications.addNotification({
      title: t('error.titles.' + code),
      text: t('error.messages.' + code),
      type: 'error',
      time: 10000
    })
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    @closeModal="closeModal">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('vacationAccumulationRequests.list.title')" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('vacationAccumulationRequests.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="vacationAccumulationRequests" />
          <DBtn
            @click.prevent="vacationAccumulationRequests"
            v-html="t('form.button.search')" />
        </div>
        <div class="flex justify-end gap-2">
          <DBtn
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
        </div>
      </div>
    </div>
    <DTable
      ref="tableVacationAccumulationRequests"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), vacationAccumulationRequests()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), vacationAccumulationRequests()">
      <template #listResult="data: any">
        <div class="flex items-center">
          <div class="text-sm" v-if="data.result">
            <template v-if="data.result.code === 'SUCCESS'">
              <div
                v-html="
                  t(
                    'vacationAccumulationRequests.observations.accumulationDetails',
                    {
                      normalDays: data.result.result.data.normalDays,
                      bonusDays: data.result.result.data.bonusDays,
                      liquidationDays: data.result.result.data.liquidationDays
                    }
                  )
                "></div>
              <template
                v-if="
                  data.result.result.warnings &&
                  data.result.result.warnings.length > 0
                ">
                <div v-for="warning in data.result.result.warnings">
                  <p class="text-yellow-600 mt-2 font-semibold">
                    {{
                      t('vacationAccumulationRequests.observations.precautions')
                    }}
                  </p>
                  <div
                    v-if="warning.code === 'PERIOD_CREATED_WARNING'"
                    class="flex items-center gap-2">
                    <div
                      class="p-0.5 w-1.5 h-1.5 rounded-full bg-yellow-600"></div>
                    <div>
                      {{
                        t(
                          'vacationAccumulationRequests.observations.newPeriodCreation',
                          {
                            startDate: dayjs(warning.data.startDate).format(
                              'DD/MM/YYYY'
                            ),
                            endDate: dayjs(warning.data.endDate).format(
                              'DD/MM/YYYY'
                            )
                          }
                        )
                      }}
                    </div>
                  </div>
                </div>
              </template>
            </template>
            <template v-else-if="data.result.code === 'UNKNOWN_ERROR'">
              <div>
                {{
                  t('vacationAccumulationRequests.observations.unexpectedError')
                }}
              </div>
            </template>
            <template
              v-else-if="data.result.code === 'INVALID_BALANCE_STATUS_ERROR'">
              <div
                v-if="
                  data.result.result.data.balanceStatus === 'VERY_OUTDATED'
                ">
                {{
                  t(
                    'vacationAccumulationRequests.observations.outdatedBalances',
                    {
                      days: data.result.result.data.daysSinceLastAccumulation,
                      date: dayjs(
                        data.result.result.data.lastAccumulationDate
                      ).format('DD/MM/YYYY')
                    }
                  )
                }}
              </div>
              <div
                v-else-if="data.result.result.data.balanceStatus === 'UPDATED'">
                {{
                  t(
                    'vacationAccumulationRequests.observations.previousAccumulation',
                    {
                      date: dayjs(
                        data.result.result.data.lastAccumulationDate
                      ).format('DD/MM/YYYY')
                    }
                  )
                }}
              </div>
            </template>
            <template
              v-else-if="
                data.result.code === 'EMPLOYEE_DOES_NOT_HAVE_PERIOD_OPEN_ERROR'
              ">
              <div>
                {{
                  t('vacationAccumulationRequests.observations.noOpenPeriod')
                }}
              </div>
            </template>
            <template
              v-else-if="data.result.code === 'EMPLOYEE_NOT_FOUND_ERROR'">
              <div>
                {{
                  t(
                    'vacationAccumulationRequests.observations.employeeNotFound'
                  )
                }}
              </div>
            </template>
          </div>
        </div>
      </template>
      <template #statusProcess="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.statusCode === 'NOSTART'
              ? 'bg-gray-100 text-gray-600'
              : data.statusCode === 'PENDING'
              ? 'bg-blue-100 text-blue-600'
              : data.statusCode === 'ERROR'
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="
              data.statusCode === 'NOSTART'
                ? 'bg-gray-600'
                : data.statusCode === 'PENDING'
                ? 'bg-blue-600'
                : data.statusCode === 'ERROR'
                ? 'bg-red-600'
                : 'bg-green-600'
            "></div>
          {{ t('status.' + data.statusCode) }}
        </div>
      </template>
    </DTable>
  </div>
</template>
